import { normalizeFieldKey } from "../../Components/ContentItemForm";
import { editableMarkdownToPage } from "../../Components/PageEditor/editableMarkdownToPage";
import { componentsForPayload, normalizeComponents } from "../../Components/PageEditor/pageEditorUtils";
import type { ContentTypeConfig } from "../../API/Models";

export const DEFAULT_LANGUAGE = "fr";
export const DEFAULT_DOMAIN = process.env.REACT_APP_PREVIEW_SITE_URL || "https://apa-parts.preview.pulsarinteractive.io";
export const PAGE_SIZE_OPTIONS = [15, 30, 50];
export const SINGLETON_KEYS = new Set(["layout", "languages", "contact-info"]);

/**
 * Content types that must NOT appear in the "Browse & manage" overview or its type dropdown:
 *  - the layout / languages / contact-info singletons are edited from their own sidebar pages, not browsed;
 *  - site-settings / sitemap are technical, non-editorial types that don't belong in the content browser.
 * Matched on the normalised key OR title so it holds regardless of per-env ids.
 */
export const HIDDEN_BROWSE_KEYS = new Set([
	"layout",
	"languages",
	"contact-info",
	"site-settings",
	"sitemap",
	"sitemaps"
]);

/** Editorial "primary" types pinned to the very top of the browse overview, in this order. */
export const PINNED_BROWSE_KEYS = ["article", "event", "page"];

export type ViewMode = "list" | "grid";
export type ContentTypeFilter = "all" | string;

export const getQueryType = (search: string) => {
	const params = new URLSearchParams(search);
	return params.get("type") ?? "all";
};

export const normalizeContentTypeKey = (value?: string | null) =>
	(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

/** True when a content type should be hidden from the "Browse & manage" overview + dropdown. */
export const isHiddenBrowseType = (key?: string | null, title?: string | null): boolean => {
	const k = normalizeContentTypeKey(key ?? "");
	const t = normalizeContentTypeKey(title ?? "");
	return HIDDEN_BROWSE_KEYS.has(k) || HIDDEN_BROWSE_KEYS.has(t);
};

/**
 * Browse-overview ordering: the pinned editorial types first (article → event → page), then the
 * `isMain` "massive" content types, then everything else — alphabetical within each tier. So the data
 * an editor touches every day sits at the top, even when a type (e.g. event) is currently empty.
 */
export const compareBrowseOrder = (
	a: { key?: string | null; title?: string | null; isMain?: boolean | null },
	b: { key?: string | null; title?: string | null; isMain?: boolean | null }
): number => {
	const rank = (x: { key?: string | null; title?: string | null; isMain?: boolean | null }) => {
		// Match the pin against BOTH the key and the title — a type's id can be a per-env hex, but its
		// title still normalises to "article" / "event" / "page".
		const idxOf = (v?: string | null) => {
			const i = PINNED_BROWSE_KEYS.indexOf(normalizeContentTypeKey(v ?? ""));
			return i < 0 ? Number.POSITIVE_INFINITY : i;
		};
		const pinned = Math.min(idxOf(x.key), idxOf(x.title));
		if (Number.isFinite(pinned)) return pinned; // 0,1,2 — pinned editorial types
		if (x.isMain) return 100; // other "main" / massive types
		return 200; // the rest
	};
	const ra = rank(a);
	const rb = rank(b);
	if (ra !== rb) return ra - rb;
	return (a.title ?? "").localeCompare(b.title ?? "", undefined, { sensitivity: "base" });
};

/**
 * Lower-cased, diacritic-stripped form of a label, used purely for pattern matching
 * (e.g. "Réserves" → "reserves"). Kept separate from `normalizeContentTypeKey`, which
 * turns accents into "-" and is meant for building stable keys, not fuzzy matching.
 */
const deaccent = (value?: string | null) =>
	(value ?? "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "");

export type ContentCategoryId =
	| "editorial"
	| "rates"
	| "securities"
	| "stats"
	| "reports"
	| "institutions"
	| "other";

export interface ContentCategory {
	id: ContentCategoryId;
	label: string;
	hint: string;
}

/**
 * Visual groups for the "Browse & manage" overview, in DISPLAY order — the domains whose
 * data changes most often sit at the top (editorial is published continuously; FX/rates
 * are updated every business day), the slow-moving registries at the bottom. Each card is
 * assigned to exactly one group by {@link categorizeContentType}; empty groups are hidden.
 */
export const CONTENT_CATEGORIES: ContentCategory[] = [
	{
		id: "editorial",
		label: "Actualités & communication",
		hint: "Articles, communiqués, événements, vidéos et pages — publiés en continu."
	},
	{
		id: "rates",
		label: "Taux & marchés",
		hint: "Cours de change, taux directeur et inflation — mis à jour quotidiennement."
	},
	{
		id: "securities",
		label: "Adjudications & titres",
		hint: "Bons APA, Bons du Trésor, encours et résultats d'adjudication."
	},
	{
		id: "stats",
		label: "Statistiques monétaires & économiques",
		hint: "Monnaie, secteurs, réserves et agrégats publiés périodiquement."
	},
	{
		id: "reports",
		label: "Rapports & publications",
		hint: "Rapports, bulletins, notes de conjoncture et documents PDF."
	},
	{
		id: "institutions",
		label: "Institutions & supervision",
		hint: "Registres des intermédiaires financiers, gouverneurs et supervision."
	},
	{ id: "other", label: "Autres", hint: "Autres types de contenu." }
];

/**
 * MATCH priority (independent of display order): the financial / structural domains are
 * tested before the editorial catch-all so e.g. "Bons APA — Annonce de l'adjudication"
 * lands in Adjudications rather than Actualités on the bare word "annonce". First match
 * on the de-accented title wins; nothing matched → "other".
 */
const CATEGORY_MATCHERS: Array<{ id: ContentCategoryId; patterns: string[] }> = [
	{ id: "rates", patterns: ["taux", "cours de change", "cours parallele"] },
	{
		id: "securities",
		patterns: [
			"bons",
			"adjudication",
			"encours",
			"refinancement",
			"titres indexes",
			"indice d ajustement",
			"facilites permanentes",
			"prets a court terme"
		]
	},
	{
		id: "stats",
		patterns: [
			"monnaie et credit",
			"secteur exterieur",
			"secteur reel",
			"reserve",
			"coefficient",
			"finances publiques",
			"volume de transaction",
			"bulletin",
			"conden",
			"statistiq"
		]
	},
	{ id: "reports", patterns: ["rapport", "publication", "conjoncture", "fichiers pdf", "dsif"] },
	{
		id: "institutions",
		patterns: [
			"if ",
			"institution",
			"etablissement",
			"billets de banque",
			"gouverneur",
			"surveillance",
			"intermediaires financiers",
			"dsp"
		]
	},
	{ id: "editorial", patterns: ["article", "actualit", "communiqu", "annonce", "event", "video", "page"] }
];

/** Assign a content type to one of the {@link CONTENT_CATEGORIES} for the overview grouping. */
export const categorizeContentType = (title?: string | null, key?: string | null): ContentCategoryId => {
	const hay = `${deaccent(title)} ${deaccent(key)}`;
	for (const matcher of CATEGORY_MATCHERS) {
		if (matcher.patterns.some((pattern) => hay.includes(pattern))) return matcher.id;
	}
	return "other";
};

export const resolveDefaultFieldKey = (field: { key?: string | null; title?: string | null }, index: number) =>
	normalizeFieldKey(field.key ?? field.title ?? `field-${index}`);

export const buildTodayValue = () => {
	const now = new Date();
	const day = String(now.getDate()).padStart(2, "0");
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const year = String(now.getFullYear());
	return `${day}/${month}/${year}`;
};

export const buildDefaultComponentsFromMarkdown = (markdown: string) => {
	if (!markdown.trim()) return [];
	try {
		const page = editableMarkdownToPage(markdown, null, DEFAULT_LANGUAGE);
		return componentsForPayload(normalizeComponents(page.components));
	} catch {
		return [];
	}
};

export const resolveContentTypeConfig = (configs: ContentTypeConfig[], idOrKey?: string | null) => {
	if (!idOrKey) return null;
	const normalized = idOrKey.trim().toLowerCase();
	const byId = configs.find((item) => (item.id ?? "").toLowerCase() === normalized);
	if (byId) return byId;
	return configs.find((item) => normalizeContentTypeKey(item.title ?? "") === normalized) ?? null;
};

export const countByKey = (value: any, keyName: string): number => {
	if (value == null) return 0;
	if (Array.isArray(value)) return value.reduce((acc, v) => acc + countByKey(v, keyName), 0);
	if (typeof value === "object") {
		let sum = 0;
		for (const [k, v] of Object.entries(value)) {
			if (k === keyName && typeof v === "string" && (v as string).trim().length > 0) sum += 1;
			sum += countByKey(v, keyName);
		}
		return sum;
	}
	return 0;
};

export const countLinks = (p: any) => countByKey(p, "href");
export const countReferences = (p: any) => countByKey(p, "id");

/**
 * Internal/migration bookkeeping fields that must never surface as a column in the
 * content table — e.g. the imported Drupal node id. Matched on key OR title so it
 * holds whatever the field is labelled, for any content type.
 */
export const isHiddenListField = (field: { key?: string | null; title?: string | null }): boolean => {
	const hay = `${field.key ?? ""} ${field.title ?? ""}`.toLowerCase();
	return /drupal/.test(hay) || /\bnode[\s_-]*id\b/.test(hay) || /\bnid\b/.test(hay);
};

/**
 * Public URL path for a content item — records use their resolved dynamic `path`,
 * pages use their `slug`. Returns "" when the item has no public page (so no
 * preview link is offered). Built purely from data already in the list payload, so
 * resolving a preview link is instant (no extra request).
 */
export const publicPathForItem = (p: {
	contentType?: string | null;
	path?: string | null;
	slug?: string | null;
}): string => {
	const isPage = (p?.contentType ?? "").toLowerCase() === "page";
	let raw = (isPage ? p?.slug : p?.path ?? p?.slug) ?? "";
	if (!raw) return "";
	try {
		raw = decodeURIComponent(raw);
	} catch {
		/* keep raw */
	}
	return String(raw).replace(/^\/+/, "");
};

export const formatListFieldValue = (value: any): string => {
	if (value == null) return "\u2014";
	if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
	if (Array.isArray(value)) {
		const parts = value.map((entry) => formatListFieldValue(entry)).filter((entry) => entry && entry !== "\u2014");
		return parts.length > 0 ? parts.join(", ") : "\u2014";
	}
	try {
		return JSON.stringify(value);
	} catch {
		return "\u2014";
	}
};
